import { useState, useEffect, useCallback } from 'react';
import api from '../services/api';

export interface ApprovalProgressStep {
  stepOrder: number;
  name: string;
  status: 'Pending' | 'Queued' | 'Approved' | 'Rejected' | 'Skipped' | 'Delegated' | 'Escalated' | 'Cancelled';
  assigneeName: string | null;
  actedAt: string | null;
  note: string | null;
}

// When a requester's line manager also holds the IT Manager role, planApproval
// auto-skips the Line Manager step and routes the real (Pending) step to that same
// person under the IT Manager step instead — "always one approver". Shown raw, this
// reads as a confusing "Line Manager: Skipped" followed by a second step for the same
// name. Collapse the two into one row, using the earlier step's name (the one the
// requester recognizes — "Line Manager") but the live step's actual status/note.
function mergeCollapsedSteps(steps: ApprovalProgressStep[]): ApprovalProgressStep[] {
  const merged: ApprovalProgressStep[] = [];
  for (let i = 0; i < steps.length; i++) {
    const cur = steps[i];
    const next = steps[i + 1];
    if (cur.status === 'Skipped' && next && cur.assigneeName && cur.assigneeName === next.assigneeName) {
      merged.push({ ...next, name: cur.name });
      i++;
      continue;
    }
    merged.push(cur);
  }
  return merged;
}

export function useApprovalProgress(ticketId: string | undefined) {
  const [steps, setSteps] = useState<ApprovalProgressStep[]>([]);
  const [loading, setLoading] = useState(true);

  // `silent` skips the loading flag — used by the background poll so the panel
  // doesn't blank out every cycle (ApprovalStepper renders nothing while loading).
  const refetch = useCallback(async (opts?: { silent?: boolean }) => {
    if (!ticketId) return;
    if (!opts?.silent) setLoading(true);
    try {
      const res = await api.get(`/tickets/${ticketId}/approvals`);
      setSteps(mergeCollapsedSteps(res.data.steps ?? []));
    } catch {
      /* silent */
    } finally {
      if (!opts?.silent) setLoading(false);
    }
  }, [ticketId]);

  // Poll so another user's approve/reject shows up here without a manual refresh.
  useEffect(() => {
    refetch();
    const interval = setInterval(() => refetch({ silent: true }), 15000);
    return () => clearInterval(interval);
  }, [refetch]);

  return { steps, loading, refetch };
}
